import { FetchBaseQueryArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { CONFIG } from 'config/config';
import { RootState } from 'redux/store';
import { Mutex } from 'async-mutex';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { handleRefreshToken } from 'utils/services/api.service';
import { setLogOut } from 'redux/slices/Auth/authSlice';
import { HttpStatusCodeEnum } from 'types/interfaces/ErrorApi';
import { isThrottlerError } from 'utils/helpers/api-url';
import { setSnackBarConfig } from 'redux/slices/app/appSlice';
import { ESnackBarSeverity } from 'types/interfaces/SnachBar';
import { translate } from 'locales/i18n';
import { buildArgsWithApiKey } from 'utils/helpers/api-key-request.helpers';

const mutex = new Mutex();

export const baseQuery: FetchBaseQueryArgs = {
  baseUrl: CONFIG.BASE_URL_API,

  prepareHeaders: (headers: Headers, { getState }) => {
    const token = (getState() as RootState).authReducer.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('x-client-type', 'bo');

    return headers;
  },
};
const fetchWithBaseQuery = fetchBaseQuery(baseQuery);

export const baseQueryConfig: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  const modifiedArgs = await buildArgsWithApiKey(args);

  let result = await fetchWithBaseQuery(modifiedArgs, api, extraOptions);

  if (isThrottlerError(result)) {
    api.dispatch(
      setSnackBarConfig({
        open: true,
        severity: ESnackBarSeverity.ERROR,
        title: 'snack.error',
        message: translate('errorApi.THROTTLER_LIMIT_EXCEEDED'),
      }),
    );
    return result;
  }

  // handle refresh token logic
  const rememberMe = (api.getState() as RootState).authReducer.rememberMe;
  const accessToken = (api.getState() as RootState).authReducer.token;

  if (
    accessToken &&
    accessToken.length > 0 &&
    result.error &&
    result.error.status === HttpStatusCodeEnum.UNAUTHORIZED &&
    rememberMe
  ) {
    if (!mutex.isLocked()) {
      result = await handleRefreshToken(result, api, extraOptions, modifiedArgs, mutex);
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      const retryArgs = await buildArgsWithApiKey(args);
      result = await fetchWithBaseQuery(retryArgs, api, extraOptions);
    }
  } else if (
    accessToken &&
    accessToken.length > 0 &&
    result.error &&
    result.error.status === HttpStatusCodeEnum.UNAUTHORIZED &&
    !rememberMe
  ) {
    api.dispatch(setLogOut());
  }

  return result;
};
